import * as p from '@clack/prompts'
import type { AuthResult, VercelRunner } from '@cli-types/next-adapter/vercel.js'
import { LOGIN_TIMEOUT_MS } from '@cli-constants/timeouts.js'
import { clackLogRows, eraseClackLine, eraseRows, spinner } from '@cli-utils/terminal.js'
import pc from 'picocolors'
import { runVercel } from '../runner/run-vercel.js'
import { checkVercelAuth } from './check-vercel-auth.js'
import { showVercelLoginLine } from './show-vercel-login-line.js'
import { signedInMessage } from './signed-in-message.js'

/** `vercel login` inherits the TTY, so callers must have no spinner running when calling this. */
export async function ensureVercelAuth(
  runner: VercelRunner,
  cwd: string,
  options: {
    env?: NodeJS.ProcessEnv
    quietIfAuthed?: boolean
    allowLogin?: boolean
  }
): Promise<AuthResult> {
  const env = options.env ?? process.env

  const checkSpinner = spinner()
  checkSpinner.start(
    runner.source === 'npx' ? 'Checking Vercel, This may take a moment' : 'Checking Vercel'
  )
  const existing = await checkVercelAuth(runner, cwd, env)
  if (existing.authed) {
    if (options.quietIfAuthed) {
      checkSpinner.clear()
      return existing
    }
    checkSpinner.stop(signedInMessage(existing.username))
    return { ...existing, dismissNote: eraseClackLine }
  }

  // A set VERCEL_TOKEN means the run may be headless, where a browser login can never complete.
  if (env.VERCEL_TOKEN) {
    checkSpinner.stop('Could not verify VERCEL_TOKEN with Vercel')
    return { authed: false, reason: 'login-failed' }
  }

  if (options.allowLogin === false) {
    checkSpinner.clear()
    return { authed: false, reason: existing.reason }
  }

  checkSpinner.clear()

  const signInMessage = `Sign in to Vercel to continue ${pc.dim('(or press Ctrl-C to enter a connection string manually)')}`
  const signInRows = clackLogRows(signInMessage)
  p.log.info(signInMessage)

  let streamedRows = 0
  const login = await runVercel(runner, ['login'], {
    cwd,
    mode: 'stream',
    streamLineFilter: (line) => {
      const show = showVercelLoginLine(line)
      if (show) streamedRows += 1
      return show
    },
    timeoutMs: LOGIN_TIMEOUT_MS,
    env
  })

  if (!login.success) {
    return { authed: false, reason: login.timedOut ? 'timeout' : 'login-cancelled' }
  }

  eraseRows(signInRows + streamedRows)

  const verifySpinner = spinner()
  verifySpinner.start('Verifying Vercel sign-in')
  const after = await checkVercelAuth(runner, cwd, env)
  if (after.authed) {
    verifySpinner.stop(signedInMessage(after.username))
    return { ...after, dismissNote: eraseClackLine }
  }
  verifySpinner.stop('Could not verify your Vercel sign-in')
  return { authed: false, reason: 'login-failed' }
}
